import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, FileText, Clock3, ShieldAlert, Loader2 } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { ALL_SHOPS_ID } from '../utils/currentShop';
import { GdprClientConsentNotice } from '../components/GdprClientConsentNotice';
import { isMinor, type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { DateTimeInput } from '../components/DateTimeInput';
import { DateInput } from '../components/DateInput';
import { localizeAppointmentApiMessage } from '../utils/localizeApiMessage';

interface AppointmentFormData {
  clientId: string;
  practitionerId: string; // empty string means "unassigned"
  shopId: string;
  serviceId: string;
  scheduledAt: string;
  estimatedEndTime: string;
  status: string;
  type: string;
  notes: string;
  clientNotes: string;
  pricing: {
    basePrice: number;
    deposit: number;
    total: number;
    paymentMethod: string;
    currency: string;
  };
}

interface AppointmentCreatePageProps {
  embedded?: boolean;
  initialData?: {
    shopId?: string;
    practitionerId?: string | null;
    scheduledAt?: string;
    estimatedEndTime?: string;
  };
  onClose?: () => void;
  onCreated?: (appointmentId?: string) => void | Promise<void>;
}

interface SelectOption {
  id: string;
  name: string;
  price?: number;
  estimatedDuration?: number;
  planningRecommendation?: string | null;
  category?: string;
  isMinor?: boolean | null;
  dateOfBirth?: string | null;
}

interface AppointmentDurationPreview {
  durationMinutes: number;
  serviceDurationMinutes: number;
  regularSlotMinutes: number;
  minorSlotMinutes: number;
  isMinor: boolean;
  actCount: number;
  ruleType: string;
}

interface BodyZoneOption {
  id: string;
  name: string;
}

interface PiercingTypeOption {
  id: string;
  name: string;
  bodyZoneId: string;
  basePrice?: number;
  estimatedDuration?: number;
}

interface RequestedPiercingItem {
  bodyZoneId: string;
  piercingTypeId: string;
}

interface QuickClientFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  isMinor: boolean | null;
  consentGiven: boolean;
}

interface ShopPiercingOffering {
  id: string;
  customPrice?: number | null;
  customDurationMinutes?: number | null;
  customMinorDurationMinutes?: number | null;
  basePrice?: number | null;
  estimatedDuration?: number | null;
  isOffered?: boolean;
  isAvailable?: boolean;
}

const getAutoTypeLabelFromCategory = (category?: string): string | null => {
  if (!category) return null;
  if (category === 'PIERCING') return 'Piercing';
  if (category === 'TATTOO') return 'Seance tatouage';
  if (category === 'CONSULTATION') return 'Consultation';
  if (category === 'REMOVAL') return 'Detatouage';
  if (category === 'AFTERCARE') return 'Suivi';
  return null;
};

const roundPrice = (value: number): number => Math.round(value * 100) / 100;

const byLocalizedName = <T extends { name: string }>(left: T, right: T): number =>
  left.name.localeCompare(right.name, 'fr', { sensitivity: 'base', numeric: true });

const getDurationMinutes = (startDateTimeLocal: string, endDateTimeLocal: string): number | null => {
  if (!startDateTimeLocal || !endDateTimeLocal) return null;
  const start = new Date(startDateTimeLocal);
  const end = new Date(endDateTimeLocal);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  return minutes > 0 ? minutes : null;
};

const addMinutesToDateTimeLocal = (dateTimeLocal: string, minutes: number): string => {
  if (!dateTimeLocal) return '';
  const date = new Date(dateTimeLocal);
  if (Number.isNaN(date.getTime())) return '';
  date.setMinutes(date.getMinutes() + Math.max(0, Math.round(minutes)));
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${mins}`;
};

export function AppointmentCreatePage({
  embedded = false,
  initialData,
  onClose,
  onCreated,
}: AppointmentCreatePageProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentShopId, setCurrentShopId } = useCurrentShop();
  
  const [formData, setFormData] = useState<AppointmentFormData>({
    clientId: '',
    practitionerId: initialData?.practitionerId || '',
    shopId: initialData?.shopId || '',
    serviceId: '',
    scheduledAt: initialData?.scheduledAt || '',
    estimatedEndTime: initialData?.estimatedEndTime || '',
    status: 'SCHEDULED',
    type: 'CONSULTATION',
    notes: '',
    clientNotes: '',
    pricing: {
      basePrice: 0,
      deposit: 0,
      total: 0,
      paymentMethod: 'card',
      currency: 'EUR'
    }
  });
  
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [practitioners, setPractitioners] = useState<SelectOption[]>([]);
  const [shops, setShops] = useState<SelectOption[]>([]);
  const [services, setServices] = useState<SelectOption[]>([]);
  const [bodyZones, setBodyZones] = useState<BodyZoneOption[]>([]);
  const [piercingTypes, setPiercingTypes] = useState<PiercingTypeOption[]>([]);
  const [shopPiercingOfferings, setShopPiercingOfferings] = useState<Record<string, ShopPiercingOffering>>({});
  const [requestedPiercings, setRequestedPiercings] = useState<RequestedPiercingItem[]>([
    { bodyZoneId: '', piercingTypeId: '' },
  ]);
  const [clientSearch, setClientSearch] = useState('');
const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [createClientError, setCreateClientError] = useState<string | null>(null);
  const [durationPreview, setDurationPreview] = useState<AppointmentDurationPreview | null>(null);
  const [durationPreviewLoading, setDurationPreviewLoading] = useState(false);
  // Staff may lengthen the appointment; the computed duration then only acts as a minimum.
  const [endTimeManuallySet, setEndTimeManuallySet] = useState(false);
  const [quickClientData, setQuickClientData] = useState<QuickClientFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null,
    consentGiven: false,
  });
  const [quickClientPhoneCountry, setQuickClientPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [quickClientPhoneDisplay, setQuickClientPhoneDisplay] = useState('');
  const [quickClientPhoneE164, setQuickClientPhoneE164] = useState<string | null>(null);
  const [quickClientPhoneIsValid, setQuickClientPhoneIsValid] = useState(false);
const filteredClients = clients
  .filter((client) =>
    client.name.toLowerCase().includes(clientSearch.trim().toLowerCase())
  )
  .slice(0, 50);

const selectedClient = clients.find(
  (client) => client.id === formData.clientId
);
  useEffect(() => {
    fetchSelectOptions();
  }, []);

  // Keep form shop in sync with the global CurrentShopSelector
  useEffect(() => {
    if (embedded && initialData?.shopId) return;
    if (!currentShopId || currentShopId === ALL_SHOPS_ID) return;
    setFormData((prev) => {
      if (prev.shopId === currentShopId) return prev;
      return {
        ...prev,
        shopId: currentShopId,
        serviceId: '',
        practitionerId: '',
      };
    });
  }, [currentShopId, embedded, initialData?.shopId]);

  useEffect(() => {
    fetchServicesForShop(formData.shopId || undefined);
  }, [formData.shopId]);

  useEffect(() => {
    fetchPractitionersForSelection(formData.shopId, formData.serviceId);
  }, [formData.shopId, formData.serviceId]);

  useEffect(() => {
    fetchShopPiercingOfferings(formData.shopId || undefined);
  }, [formData.shopId]);

  const resolveDefaultShopId = (shopOptions: SelectOption[]): string => {
    if (!shopOptions.length) return '';
    // In "all shops" mode (or no selection), leave unselected so all shops remain choosable
    if (currentShopId && currentShopId !== ALL_SHOPS_ID && shopOptions.some((shop) => shop.id === currentShopId)) {
      return currentShopId;
    }
    return shopOptions.length === 1 ? shopOptions[0].id : '';
  };

  // Keep the current duration when the start moves, either because no service drives it
  // or because staff chose the end time themselves.
  const previousScheduledAtRef = useRef(formData.scheduledAt);
  useEffect(() => {
    const previousScheduledAt = previousScheduledAtRef.current;
    previousScheduledAtRef.current = formData.scheduledAt;

    if (!formData.scheduledAt) return;
    if (!endTimeManuallySet && services.some((service) => service.id === formData.serviceId)) return;

    setFormData((prev) => {
      const durationMinutes = getDurationMinutes(previousScheduledAt, prev.estimatedEndTime) ?? 60;
      const nextEndTime = addMinutesToDateTimeLocal(formData.scheduledAt, durationMinutes);
      if (!nextEndTime || nextEndTime === prev.estimatedEndTime) return prev;
      return { ...prev, estimatedEndTime: nextEndTime };
    });
  }, [formData.scheduledAt, formData.serviceId, services, endTimeManuallySet]);

  const plannedActCount = useMemo(
    () =>
      formData.type === 'PIERCING'
        ? Math.max(1, requestedPiercings.filter((item) => item.bodyZoneId && item.piercingTypeId).length)
        : 1,
    [formData.type, requestedPiercings],
  );

  // Catalogue estimate, used for pricing and as the duration until the shop rules answer.
  const catalogEstimate = useMemo(() => {
    const selectedService = services.find((s) => s.id === formData.serviceId);
    if (!selectedService) return null;

    let durationMinutes = Math.max(1, Number(selectedService.estimatedDuration || 60));
    let basePrice = Number(selectedService.price || 0);

    if (formData.type === 'PIERCING') {
      const selectedPiercingTypeIds = requestedPiercings
        .filter((item) => item.bodyZoneId && item.piercingTypeId)
        .map((item) => item.piercingTypeId);

      if (selectedPiercingTypeIds.length > 0) {
        durationMinutes = 0;
        basePrice = 0;
        for (const piercingTypeId of selectedPiercingTypeIds) {
          const typeDef = piercingTypes.find((pt) => pt.id === piercingTypeId);
          const offering = shopPiercingOfferings[piercingTypeId];
          durationMinutes += Math.max(
            1,
            Number(offering?.customDurationMinutes ?? selectedService.estimatedDuration ?? 30),
          );
          const piercingPrice = Number(offering?.customPrice ?? offering?.basePrice ?? typeDef?.basePrice ?? 0);
          basePrice += piercingPrice;
        }
      }
    }

    return { durationMinutes, basePrice: roundPrice(basePrice) };
  }, [
    formData.serviceId,
    formData.type,
    services,
    requestedPiercings,
    piercingTypes,
    shopPiercingOfferings,
  ]);

  useEffect(() => {
    if (!catalogEstimate) return;
    setFormData((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        basePrice: catalogEstimate.basePrice,
        total: catalogEstimate.basePrice,
        deposit: roundPrice(catalogEstimate.basePrice * 0.5),
      },
    }));
  }, [catalogEstimate]);

  useEffect(() => {
    if (!formData.shopId || !formData.serviceId || !formData.clientId) {
      setDurationPreview(null);
      setDurationPreviewLoading(false);
      return;
    }

    const controller = new AbortController();
    setDurationPreview(null);
    setDurationPreviewLoading(true);

    const loadDurationPreview = async () => {
      try {
        const query = new URLSearchParams({
          shopId: formData.shopId,
          serviceId: formData.serviceId,
          clientId: formData.clientId,
          actCount: String(plannedActCount),
        });
        const piercingTypeIds = requestedPiercings
          .map((piercing) => piercing.piercingTypeId)
          .filter(Boolean);
        if (piercingTypeIds.length > 0) {
          query.set('piercingTypeIds', piercingTypeIds.join(','));
        }
        const response = await apiClient(`/api/admin/appointments/duration-preview?${query.toString()}`, {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Unable to calculate appointment duration');
        }
        setDurationPreview(data.data as AppointmentDurationPreview);
      } catch (previewError) {
        if ((previewError as Error).name !== 'AbortError') {
          console.error('Appointment duration preview error:', previewError);
          setDurationPreview(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setDurationPreviewLoading(false);
        }
      }
    };

    void loadDurationPreview();
    return () => controller.abort();
  }, [formData.shopId, formData.serviceId, formData.clientId, plannedActCount, requestedPiercings]);

  // Single owner of the end time: shop rules when known, catalogue duration otherwise.
  const effectiveDurationMinutes = durationPreview?.durationMinutes ?? catalogEstimate?.durationMinutes ?? null;
  const selectedClientIsMinor = selectedClient?.dateOfBirth
    ? isMinor(selectedClient.dateOfBirth)
    : selectedClient?.isMinor;
  const clientIsMinor = selectedClientIsMinor === true || durationPreview?.isMinor === true;

  // A new prestation changes the required duration, so the computed end time takes over again.
  useEffect(() => {
    setEndTimeManuallySet(false);
  }, [formData.serviceId, formData.clientId, plannedActCount]);

  useEffect(() => {
    if (endTimeManuallySet) return;
    if (!formData.scheduledAt || !effectiveDurationMinutes) return;
    const estimatedEndTime = addMinutesToDateTimeLocal(formData.scheduledAt, effectiveDurationMinutes);
    if (!estimatedEndTime) return;
    setFormData((prev) => (prev.estimatedEndTime === estimatedEndTime ? prev : { ...prev, estimatedEndTime }));
  }, [formData.scheduledAt, effectiveDurationMinutes, endTimeManuallySet]);

  const selectedDurationMinutes = getDurationMinutes(formData.scheduledAt, formData.estimatedEndTime);
  const endTimeError = useMemo(() => {
    if (!formData.scheduledAt || !formData.estimatedEndTime) return null;
    if (selectedDurationMinutes === null) {
      return 'La date et heure de fin doivent être postérieures à la date et heure de début.';
    }
    if (effectiveDurationMinutes && selectedDurationMinutes < effectiveDurationMinutes) {
      return `La durée doit être d’au moins ${effectiveDurationMinutes} min pour cette prestation (actuellement ${selectedDurationMinutes} min).`;
    }
    return null;
  }, [formData.scheduledAt, formData.estimatedEndTime, selectedDurationMinutes, effectiveDurationMinutes]);

  const fetchSelectOptions = async () => {
    try {
      setLoading(true);

      let currentItemCollectionId = '';
      try {
        const settingsRes = await apiClient('/api/admin/settings/general');
        const settingsData = await settingsRes.json();
        if (settingsData?.success) {
          currentItemCollectionId = settingsData.data?.currentItemCollectionId || '';
        }
      } catch (settingsError) {
        console.warn('Unable to load current item collection setting:', settingsError);
      }

      const itemCollectionQuery = currentItemCollectionId
        ? `&itemCollectionId=${encodeURIComponent(currentItemCollectionId)}`
        : '';
      
      // Fetch all required data for dropdowns
      const [clientsRes, shopsRes, bodyZonesRes, piercingTypesRes] = await Promise.all([
        apiClient('/api/admin/clients?limit=1000'),
        apiClient('/api/admin/shops?limit=1000'),
        apiClient(`/api/admin/body-zones?limit=1000${itemCollectionQuery}`),
        apiClient(`/api/admin/piercing-types?limit=1000${itemCollectionQuery}`),
      ]);

      const [clientsData, shopsData, bodyZonesData, piercingTypesData] = await Promise.all([
        clientsRes.json(),
        shopsRes.json(),
        bodyZonesRes.json(),
        piercingTypesRes.json(),
      ]);

      if (clientsData.success) {
        setClients(clientsData.data.clients.map((c: any) => ({
          id: c.id,
          name: `${c.firstName} ${c.lastName} (${c.email})`,
          isMinor:
            c.isMinor === true
            || c.isMinor === 1
            || c.isMinor === '1'
            || c.isMinor === 'true'
            || c.isMinor === 't',
          dateOfBirth: c.dateOfBirth || null,
        })));
      }

      if (shopsData.success) {
        const nextShops = shopsData.data.shops.map((s: any) => ({
          id: s.id,
          name: `${s.name} - ${s.address.city}`
        }));
        setShops(nextShops);
        setFormData((prev) => {
          if (prev.shopId) return prev;
          const defaultShopId = resolveDefaultShopId(nextShops);
          return defaultShopId ? { ...prev, shopId: defaultShopId } : prev;
        });
      }
      if (bodyZonesData.success) {
        setBodyZones((bodyZonesData.data.bodyZones || []).map((z: any) => ({
          id: z.id,
          name: z.name,
        })).sort(byLocalizedName));
      }
      if (piercingTypesData.success) {
        setPiercingTypes((piercingTypesData.data.piercingTypes || []).map((pt: any) => ({
          id: pt.id,
          name: pt.name,
          bodyZoneId: pt.bodyZoneId,
          basePrice: Number(pt.basePrice || 0),
          estimatedDuration: Number(pt.estimatedDuration || 0),
        })).sort(byLocalizedName));
      }
      await fetchServicesForShop(formData.shopId || undefined);

    } catch (error) {
      console.error('Fetch select options error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const fetchServicesForShop = async (shopId?: string) => {
    try {
      const endpoint = shopId
        ? `/api/admin/services?limit=1000&shopId=${encodeURIComponent(shopId)}`
        : '/api/admin/services?limit=1000';
      const servicesRes = await apiClient(endpoint);
      const servicesData = await servicesRes.json();
      if (!servicesData.success) return;

      const nextServices = servicesData.data.services.map((s: any) => ({
        id: s.id,
        name: s.name,
        price: s.basePrice,
        estimatedDuration: s.estimatedDuration,
        planningRecommendation: s.planningRecommendation || null,
        category: s.category,
      }));
      setServices(nextServices);

      if (formData.serviceId && !nextServices.some((s: SelectOption) => s.id === formData.serviceId)) {
        setFormData(prev => ({ ...prev, serviceId: '' }));
      }
    } catch (error) {
      console.error('Fetch services for shop error:', error);
    }
  };

  const fetchShopPiercingOfferings = async (shopId?: string) => {
    if (!shopId) {
      setShopPiercingOfferings({});
      return;
    }
    try {
      const response = await apiClient(`/api/admin/shops/${encodeURIComponent(shopId)}/piercing-offerings`);
      const data = await response.json();
      if (!data?.success) {
        setShopPiercingOfferings({});
        return;
      }
      const byId: Record<string, ShopPiercingOffering> = {};
      for (const offering of data.data?.piercingOfferings || []) {
        byId[offering.id] = {
          id: offering.id,
          customPrice: offering.customPrice,
          customDurationMinutes: offering.customDurationMinutes,
          customMinorDurationMinutes: offering.customMinorDurationMinutes,
          basePrice: offering.basePrice,
          estimatedDuration: offering.estimatedDuration,
          isOffered: offering.isOffered,
          isAvailable: offering.isAvailable,
        };
      }
      setShopPiercingOfferings(byId);
    } catch (fetchError) {
      console.error('Fetch shop piercing offerings error:', fetchError);
      setShopPiercingOfferings({});
    }
  };

  const fetchPractitionersForSelection = async (shopId: string, serviceId: string) => {
    if (!shopId) {
      setPractitioners([]);
      if (formData.practitionerId) {
        setFormData((prev) => ({ ...prev, practitionerId: '' }));
      }
      return;
    }
    try {
      const shopRes = await apiClient(`/api/admin/shops/${encodeURIComponent(shopId)}/practitioners`);
      const shopData = await shopRes.json();
      if (!shopData.success) {
        setPractitioners([]);
        return;
      }

      const shopPractitioners = (shopData.data?.practitioners || [])
        .filter((p: any) => p.isActive)
        .map((p: any) => ({
          id: p.practitionerId,
          name: `${p.practitionerFirstName} ${p.practitionerLastName}`.trim(),
        }));

      if (!serviceId) {
        setPractitioners(shopPractitioners);
        if (formData.practitionerId && !shopPractitioners.some((p: SelectOption) => p.id === formData.practitionerId)) {
          setFormData((prev) => ({ ...prev, practitionerId: '' }));
        }
        return;
      }

      const serviceRes = await apiClient(`/api/admin/services/${encodeURIComponent(serviceId)}/practitioners`);
      const serviceData = await serviceRes.json();
      if (!serviceData.success) {
        setPractitioners(shopPractitioners);
        return;
      }

      const allowedServicePractitionerIds = new Set(
        (serviceData.data?.practitioners || [])
          .filter((p: any) => p.isOffered && p.isAvailable)
          .map((p: any) => p.id),
      );
      const merged = shopPractitioners.filter((p: SelectOption) => allowedServicePractitionerIds.has(p.id));
      setPractitioners(merged);
      if (formData.practitionerId && !merged.some((p: SelectOption) => p.id === formData.practitionerId)) {
        setFormData((prev) => ({ ...prev, practitionerId: '' }));
      }
    } catch (fetchError) {
      console.error('Fetch filtered practitioners error:', fetchError);
      setPractitioners([]);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    const keys = field.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const updatePiercingItem = (index: number, patch: Partial<RequestedPiercingItem>) => {
    setRequestedPiercings((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              ...patch,
              piercingTypeId: patch.bodyZoneId !== undefined ? '' : (patch.piercingTypeId ?? item.piercingTypeId),
            }
          : item,
      ),
    );
  };

  const addPiercingItem = () => {
    setRequestedPiercings((prev) => [...prev, { bodyZoneId: '', piercingTypeId: '' }]);
  };

  const removePiercingItem = (index: number) => {
    setRequestedPiercings((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleServiceChange = (serviceId: string) => {
    handleInputChange('serviceId', serviceId);
    const selectedService = services.find((service) => service.id === serviceId);
    if (selectedService?.category === 'PIERCING') {
      handleInputChange('type', 'PIERCING');
    } else if (selectedService?.category === 'TATTOO') {
      handleInputChange('type', 'TATTOO_SESSION');
    } else if (selectedService?.category === 'CONSULTATION') {
      handleInputChange('type', 'CONSULTATION');
    } else if (selectedService?.category === 'REMOVAL') {
      handleInputChange('type', 'REMOVAL');
    } else if (selectedService?.category === 'AFTERCARE') {
      handleInputChange('type', 'AFTERCARE');
    }
  };

  const resetQuickClientForm = () => {
    setQuickClientData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      isMinor: null,
      consentGiven: false,
    });
    setQuickClientPhoneCountry('FR');
    setQuickClientPhoneDisplay('');
    setQuickClientPhoneE164(null);
    setQuickClientPhoneIsValid(false);
    setCreateClientError(null);
  };

  const handleCreateClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickClientData.firstName.trim()) {
      setCreateClientError('Le prénom est requis.');
      return;
    }
    if (quickClientPhoneDisplay.trim() && (!quickClientPhoneIsValid || !quickClientPhoneE164)) {
      setCreateClientError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }
    try {
      setCreatingClient(true);
      setCreateClientError(null);
      const response = await apiClient('/api/clients/quick-create', {
        method: 'POST',
        body: JSON.stringify({
          firstName: quickClientData.firstName.trim(),
          lastName: quickClientData.lastName.trim(),
          email: quickClientData.email.trim(),
          phone: quickClientPhoneE164 ?? '',
          dateOfBirth: quickClientData.dateOfBirth || undefined,
          isMinor: quickClientData.isMinor,
          consentGiven: quickClientData.consentGiven,
        }),
      });
      const data = await response.json();
      if (!data?.success || !data?.data?.client?.id) {
        setCreateClientError(
          localizeAppointmentApiMessage(data?.message) || 'Impossible de créer le client.',
        );
        return;
      }

      const newClientOption = {
        id: data.data.client.id,
        name: `${data.data.client.firstName} ${data.data.client.lastName} (${data.data.client.email || data.data.client.phone || 'sans contact'})`,
        isMinor: Boolean(data.data.client.isMinor),
        dateOfBirth: data.data.client.dateOfBirth || null,
      };
      setClients((prev) => [newClientOption, ...prev]);

      handleInputChange('clientId', newClientOption.id);
      setClientSearch(newClientOption.name);

      setShowCreateClientModal(false);
      resetQuickClientForm();
    } catch (createError) {
      console.error('Create client from appointment error:', createError);
      setCreateClientError('Erreur de connexion au serveur.');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) {
  setError('Veuillez sélectionner un client.');
  setSaving(false);
  return;
}
    if (endTimeError) {
      setError(endTimeError);
      setSaving(false);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);
      
      // Prepare appointment data
      const { clientNotes, ...appointmentBase } = formData;
      const selectedPiercings = requestedPiercings
        .filter((item) => item.bodyZoneId && item.piercingTypeId)
        .map((item) => {
          const zone = bodyZones.find((z) => z.id === item.bodyZoneId);
          const piercingType = piercingTypes.find((pt) => pt.id === item.piercingTypeId);
          return {
            id: piercingType?.id || item.piercingTypeId,
            name: piercingType?.name || item.piercingTypeId,
            bodyZoneId: item.bodyZoneId,
            bodyZone: zone?.name || item.bodyZoneId,
          };
        });

      if (formData.type === 'PIERCING' && selectedPiercings.length === 0) {
        setError('Veuillez renseigner au moins un type de piercing demandé.');
        setSaving(false);
        return;
      }

      const nonPiercingNotes = (formData.notes || '')
        .replace(/^Piercings:\s.*?(?:\.\s*|$)/i, '')
        .trim();
      const piercingSummary = selectedPiercings.length > 0
        ? `Piercings: ${selectedPiercings.map((p) => `${p.bodyZone} - ${p.name}`).join(', ')}.`
        : '';
      const finalNotes = [piercingSummary, nonPiercingNotes].filter(Boolean).join(' ').trim();

      const appointmentData = {
        ...appointmentBase,
        notes: finalNotes,
        pricing: {
          ...appointmentBase.pricing,
          piercingTypes: selectedPiercings.length > 0 ? selectedPiercings : undefined,
          piercingType: selectedPiercings.length > 0 ? selectedPiercings[0] : undefined,
        },
        practitionerId: formData.practitionerId || null,
        preparation: {
          beforeAppointment: [],
          whatToBring: [],
          restrictions: [],
        },
        reminders: {
          enabled: true,
          methods: ['email', 'sms'],
          daysBefore: [1, 7],
          customMessage: `Rappel: Rendez-vous ${formData.type.toLowerCase()} le ${new Date(formData.scheduledAt).toLocaleDateString('fr-FR')}`
        }
      };
      
      const response = await apiClient('/api/admin/appointments', {
        method: 'POST',
        body: JSON.stringify(appointmentData),
      });

      const data = await response.json();
      
      if (data.success) {
        const newId = data.data?.id;
        if (newId) {
          await apiClient(`/api/admin/appointments/${newId}/resend-confirmation-email`, {
            method: 'POST',
          });
        }
        if (embedded) {
          await onCreated?.(newId);
        } else {
          // Redirect to edit page so the user can fill in piercing records immediately
          navigate(newId ? `/appointments/${newId}/edit` : '/appointments');
        }
      } else {
        setError(localizeAppointmentApiMessage(data.message) || 'Échec de la création du rendez-vous');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Create appointment error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (embedded) {
      onClose?.();
    } else {
      navigate('/appointments');
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'min-h-64' : 'min-h-screen'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className={`p-6 ${saving ? 'cursor-wait' : ''}`}>
      {/* Header */}
      <div className={`flex flex-wrap items-center justify-between gap-4 mb-8 ${embedded ? 'pr-12' : ''}`}>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{embedded ? 'Fermer' : 'Retour à la liste'}</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Nouveau Rendez-vous</span>
              <HelpIcon 
                docPath="07-gestion-rendez-vous#07-3-creation" 
                tooltip="Documentation : Création d'un rendez-vous"
              />
            </h1>
            <p className="text-textSecondary">Créer un nouveau rendez-vous</p>
          </div>
        </div>

        <button
          type="submit"
          form="appointment-form"
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Création...' : 'Créer le Rendez-vous'}</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form id="appointment-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Calendar className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations de Base</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Shop *
              </label>
              <select
                required
                value={formData.shopId}
                onChange={(e) => {
                  const nextShopId = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    shopId: nextShopId,
                    serviceId: '',
                    practitionerId: '',
                  }));
                  if (nextShopId) setCurrentShopId(nextShopId);
                }}
                className="input-field"
              >
                <option value="">Sélectionner un shop</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Service *
              </label>
              <select
                required
                value={formData.serviceId}
                onChange={(e) => handleServiceChange(e.target.value)}
                className="input-field"
                disabled={!formData.shopId}
              >
                <option value="">{formData.shopId ? 'Sélectionner un service' : 'Sélectionner d’abord un shop'}</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              {(() => {
                const selectedService = services.find((s) => s.id === formData.serviceId);
                const autoTypeLabel = getAutoTypeLabelFromCategory(selectedService?.category);
                if (!selectedService || !autoTypeLabel) return null;
                return (
                  <p className="mt-1 text-xs text-gray-500">
                    Type auto: <span className="font-medium text-gray-700">{autoTypeLabel}</span>
                  </p>
                );
              })()}
              {services.find((s) => s.id === formData.serviceId)?.planningRecommendation && (
                <div
                  className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                  dangerouslySetInnerHTML={{ __html: services.find((s) => s.id === formData.serviceId)?.planningRecommendation || '' }}
                />
              )}
            </div>

            <div>
              <div className="flex h-5 items-center justify-between gap-2 mb-2">
                <label className="block text-sm font-medium text-textPrimary">
                  Client *
                </label>
                <button
                  type="button"
                  onClick={() => {
                    resetQuickClientForm();
                    setShowCreateClientModal(true);
                  }}
                  className="text-xs leading-none px-2 py-0.5 border border-primary-300 text-primary-700 rounded hover:bg-primary-50"
                >
                  Nouveau client
                </button>
              </div>
              <div className="relative">
  <input
    type="text"
    value={
      clientDropdownOpen
        ? clientSearch
        : selectedClient?.name || clientSearch
    }
    onFocus={() => {
      setClientDropdownOpen(true);
      setClientSearch('');
    }}
    onChange={(e) => {
      setClientSearch(e.target.value);
      setClientDropdownOpen(true);

      // Si l'utilisateur modifie la recherche après avoir choisi
      // un client, on réinitialise la sélection.
      if (formData.clientId) {
        handleInputChange('clientId', '');
      }
    }}
    onKeyDown={(e) => {
      if (e.key === 'Escape') {
        setClientDropdownOpen(false);
      }

      if (
        e.key === 'Enter' &&
        clientDropdownOpen &&
        filteredClients.length === 1
      ) {
        e.preventDefault();

        const client = filteredClients[0];

        handleInputChange('clientId', client.id);
        setClientSearch(client.name);
        setClientDropdownOpen(false);
      }
    }}
    className="input-field"
    placeholder="Rechercher par nom, email..."
    autoComplete="off"
  />

  {clientDropdownOpen && (
    <div
      className="
        absolute z-50 mt-1 w-full
        max-h-64 overflow-y-auto
        rounded-lg border border-gray-200
        bg-white shadow-lg
      "
    >
      {filteredClients.length > 0 ? (
        filteredClients.map((client) => (
          <button
            key={client.id}
            type="button"
            onMouseDown={(e) => {
              // Empêche le blur de l'input avant le clic.
              e.preventDefault();
            }}
            onClick={() => {
              handleInputChange('clientId', client.id);
              setClientSearch(client.name);
              setClientDropdownOpen(false);
            }}
            className="
              block w-full px-3 py-2
              text-left text-sm
              hover:bg-gray-100
              focus:bg-gray-100
              focus:outline-none
            "
          >
            <span>{client.name}</span>
            {client.isMinor && (
              <span className="ml-2 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-amber-800">
                MINEUR
              </span>
            )}
          </button>
        ))
      ) : (
        <div className="px-3 py-3 text-sm text-gray-500">
          Aucun client trouvé
        </div>
      )}
    </div>
  )}

  <input
    type="hidden"
    name="clientId"
    value={formData.clientId}
  />

  {!formData.clientId && clientSearch && !clientDropdownOpen && (
    <p className="mt-1 text-xs text-red-600">
      Veuillez sélectionner un client dans la liste.
    </p>
  )}
  {selectedClient?.isMinor && (
    <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
      <div>
        <p className="text-xs font-semibold">Client mineur détecté</p>
        <p className="mt-0.5 text-xs text-amber-800">
          La durée du rendez-vous sera automatiquement adaptée aux règles mineur du shop.
        </p>
      </div>
    </div>
  )}
</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Date et heure de début *
              </label>
             <DateTimeInput
                  required
                  value={formData.scheduledAt}
                  onChange={(value) => {
                    handleInputChange('scheduledAt', value);
                  }}
                  className="input-field"
                  min={new Date().toISOString().slice(0, 16)}
                />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Date et heure de fin *
              </label>
              <DateTimeInput
                required
                value={formData.estimatedEndTime}
                onChange={(value) => {
                  setEndTimeManuallySet(true);
                  handleInputChange('estimatedEndTime', value);
                }}
                className="input-field"
                min={formData.scheduledAt || undefined}
              />
              {endTimeError && (
                <p className="mt-2 text-xs font-medium text-red-700">{endTimeError}</p>
              )}
              {formData.serviceId && (
                <div
                  className={`mt-2 rounded-lg border px-3 py-2 ${
                    clientIsMinor
                      ? 'border-amber-300 bg-amber-50 text-amber-950'
                      : 'border-blue-200 bg-blue-50 text-blue-950'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {clientIsMinor ? (
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                    ) : (
                      <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-semibold">
                        {durationPreviewLoading
                          ? 'Calcul de la durée…'
                          : clientIsMinor && durationPreview
                            ? `Durée mineur minimum : ${durationPreview.durationMinutes} min`
                            : `Durée minimum : ${effectiveDurationMinutes ?? 0} min`}
                        {!durationPreviewLoading && selectedDurationMinutes
                          ? ` · durée retenue : ${selectedDurationMinutes} min`
                          : ''}
                      </p>
                      {!durationPreviewLoading && clientIsMinor && durationPreview && (
                        <p className="mt-0.5 text-xs text-amber-800">
                          Recalcul automatique selon le type d’acte, le nombre d’actes et les règles du shop
                          ({durationPreview.regularSlotMinutes} min adulte / {durationPreview.minorSlotMinutes} min mineur).
                        </p>
                      )}
                      {!durationPreviewLoading && clientIsMinor && !durationPreview && (
                        <p className="mt-0.5 text-xs text-amber-800">
                          Client mineur : durée standard affichée, les règles mineur du shop n’ont pas pu être chargées.
                        </p>
                      )}
                      {!durationPreviewLoading && !clientIsMinor && (
                        <p className="mt-0.5 text-xs text-blue-800">
                          {formData.clientId
                            ? 'La fin est proposée selon le service, les actes et le client. Vous pouvez l’allonger.'
                            : 'Sélectionnez le client pour appliquer les règles de durée (mineur / majeur).'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('navigation.practitioners.title')} {formData.type === 'PIERCING' ? '' : '*'}
              </label>
              <select
                value={formData.practitionerId}
                onChange={(e) => handleInputChange('practitionerId', e.target.value)}
                className="input-field"
                disabled={!formData.shopId || !formData.serviceId}
              >
                <option value="">
                  {!formData.shopId
                    ? 'Sélectionnez d’abord un shop'
                    : !formData.serviceId
                      ? 'Sélectionnez d’abord un service'
                      : formData.type === 'PIERCING'
                        ? 'Non assigné (assignation plus tard)'
                        : 'Sélectionner un artiste'}
                </option>
                {practitioners.map((practitioner) => (
                  <option key={practitioner.id} value={practitioner.id}>
                    {practitioner.name}
                  </option>
                ))}
              </select>
              {formData.type === 'PIERCING' && (
                <p className="mt-1 text-xs text-gray-500">
                  Pour les piercings, vous pouvez créer le rendez-vous sans praticien; l’assignation sera faite plus tard.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Statut
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="input-field"
              >
                <option value="SCHEDULED">Programmé</option>
                <option value="CONFIRMED">Confirmé</option>
                <option value="CHECKED_IN">Arrivé</option>
                <option value="IN_PROGRESS">Pris en charge</option>
                <option value="COMPLETED">Terminé</option>
                <option value="CANCELLED">Annulé</option>
                <option value="NO_SHOW">Absent</option>
                <option value="RESCHEDULED">Reporté</option>
              </select>
            </div>

            {formData.type === 'TATTOO_SESSION' && (
              <div className="md:col-span-2 lg:col-span-3 border border-purple-200 rounded-lg p-4 bg-purple-50">
                <p className="text-sm text-purple-800">
                  Projet tatouage: renseignez les détails (zone, style, dimensions, references) dans les notes du rendez-vous.
                </p>
              </div>
            )}

            {formData.type === 'PIERCING' && (
              <div className="md:col-span-2 lg:col-span-3 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-textPrimary">
                    Type(s) de piercing demandé(s) *
                  </label>
                  <button
                    type="button"
                    onClick={addPiercingItem}
                    className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded border border-blue-200 hover:bg-blue-100"
                  >
                    Ajouter un piercing
                  </button>
                </div>
                <div className="space-y-3">
                  {requestedPiercings.map((item, index) => (
                    <div key={`piercing-item-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Zone du corps</label>
                        <select
                          value={item.bodyZoneId}
                          onChange={(e) => updatePiercingItem(index, { bodyZoneId: e.target.value })}
                          className="input-field"
                        >
                          <option value="">Sélectionner une zone</option>
                          {bodyZones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Type de piercing</label>
                        <select
                          value={item.piercingTypeId}
                          onChange={(e) => updatePiercingItem(index, { piercingTypeId: e.target.value })}
                          className="input-field"
                          disabled={!item.bodyZoneId}
                        >
                          <option value="">Sélectionner un type</option>
                          {piercingTypes
                            .filter((pt) => pt.bodyZoneId === item.bodyZoneId)
                            .map((pt) => (
                              <option key={pt.id} value={pt.id}>
                                {pt.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => removePiercingItem(index)}
                          disabled={requestedPiercings.length === 1}
                          className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Notes du rendez-vous
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Notes internes sur le rendez-vous..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Notes du client
              </label>
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                Les notes du client sont saisies par le client et ne sont pas modifiables ici.
              </div>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Tarification</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Prix de base (€) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.pricing.basePrice}
                onChange={(e) => {
                  const basePrice = parseFloat(e.target.value) || 0;
                  handleInputChange('pricing.basePrice', basePrice);
                  handleInputChange('pricing.total', basePrice);
                  handleInputChange('pricing.deposit', Math.round(basePrice * 0.5));
                }}
                className="input-field"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Acompte (€)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.pricing.deposit}
                onChange={(e) => handleInputChange('pricing.deposit', parseFloat(e.target.value) || 0)}
                className="input-field"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Total (€) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.pricing.total}
                onChange={(e) => handleInputChange('pricing.total', parseFloat(e.target.value) || 0)}
                className="input-field"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Méthode de paiement
              </label>
              <select
                value={formData.pricing.paymentMethod}
                onChange={(e) => handleInputChange('pricing.paymentMethod', e.target.value)}
                className="input-field"
              >
                <option value="card">Carte bancaire</option>
                <option value="cash">Espèces</option>
                <option value="bank_transfer">Virement</option>
                <option value="digital_wallet">Portefeuille numérique</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Devise
              </label>
              <select
                value={formData.pricing.currency}
                onChange={(e) => handleInputChange('pricing.currency', e.target.value)}
                className="input-field"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="mt-6 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Prix de base</span>
              <span className="font-medium">{formData.pricing.basePrice}€</span>
            </div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Acompte</span>
              <span className="font-medium text-green-600">{formData.pricing.deposit}€</span>
            </div>
            <div className="flex items-center justify-between text-lg font-bold border-t pt-2">
              <span>Total</span>
              <span className="text-accent-600">{formData.pricing.total}€</span>
            </div>
          </div>
        </div>
      </form>

      {showCreateClientModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-textPrimary">Créer un client</h3>
              <button
                type="button"
                onClick={() => setShowCreateClientModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Fermer
              </button>
            </div>

            {createClientError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {createClientError}
              </div>
            )}

            <form onSubmit={handleCreateClientSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">Prénom *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={quickClientData.firstName}
                    onChange={(e) => setQuickClientData((prev) => ({ ...prev, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">Nom (optionnel)</label>
                  <input
                    type="text"
                    className="input-field"
                    value={quickClientData.lastName}
                    onChange={(e) => setQuickClientData((prev) => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">Email</label>
                  <input
                    type="email"
                    className="input-field"
                    value={quickClientData.email}
                    onChange={(e) => setQuickClientData((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">Téléphone</label>
                  <InternationalPhoneInput
                    value={quickClientPhoneDisplay}
                    country={quickClientPhoneCountry}
                    onChange={(result) => {
                      setQuickClientPhoneDisplay(result.displayValue);
                      setQuickClientPhoneCountry(result.country);
                      setQuickClientPhoneE164(result.e164Value);
                      setQuickClientPhoneIsValid(result.isValid);
                      setQuickClientData((prev) => ({ ...prev, phone: result.displayValue }));

                      if (result.isValid) {
                        setCreateClientError((prev) =>
                          prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                        );
                      }
                    }}
                    selectClassName="input-field"
                    inputClassName="input-field"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">
                    Date de naissance (optionnel)
                  </label>
                  <DateInput
                    value={quickClientData.dateOfBirth}
                    disabled={quickClientData.isMinor === true && !quickClientData.dateOfBirth}
                    onChange={(value) =>
                      setQuickClientData((prev) => ({
                        ...prev,
                        dateOfBirth: value,
                        isMinor: value ? isMinor(value) : null,
                      }))
                    }
                  />
                </div>
                <label className="flex items-center gap-2 self-end pb-3 text-sm text-textPrimary">
                  <input
                    type="checkbox"
                    checked={quickClientData.isMinor === true}
                    disabled={Boolean(quickClientData.dateOfBirth)}
                    onChange={(e) =>
                      setQuickClientData((prev) => ({
                        ...prev,
                        isMinor: e.target.checked ? true : null,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  Mineur (optionnel)
                </label>
              </div>
              <GdprClientConsentNotice className="mb-2" />
              <label className="flex items-start gap-2 text-sm text-textPrimary">
                <input
                  type="checkbox"
                  checked={quickClientData.consentGiven}
                  onChange={(e) => setQuickClientData((prev) => ({ ...prev, consentGiven: e.target.checked }))}
                  className="mt-1"
                />
                <span>Consentement du client (optionnel).</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateClientModal(false)}
                  className="btn-secondary"
                  disabled={creatingClient}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={creatingClient}>
                  {creatingClient ? 'Création...' : 'Créer le client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blocking overlay while the appointment is being created */}
      {saving && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-[70] cursor-wait"
          role="alertdialog"
          aria-busy="true"
          aria-live="assertive"
        >
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Création du rendez-vous...</span>
          </div>
        </div>
      )}
    </div>
  );
}
